import {
  FGCheckboxInput,
  FGListen,
  FGNumberInput,
  FGSelectInput,
  FGTextAreaInput,
  FieldGroup,
  FieldSet,
} from "nsitools-react";
import * as React from "react";
import * as Yup from "yup";

import { DogResultsApi, SaveNewResultDto } from "../../../../../../api";
import { SmallFormGenerator } from "../../../../../../components";
import { useApiService, useCrudApi, useTl } from "../../../../../../hooks";
import { useReferential } from "../../../../../../hooks/useReferential";
import { ETLCodes } from "../../../../../../locales";

export interface IDogResultPanelProps {
  dogId: number;
  selectedResultId: number;
  onSaved: () => void;
}

export const DogResultPanel: React.FunctionComponent<IDogResultPanelProps> = ({ dogId, onSaved, selectedResultId }) => {
  const { t } = useTl();
  const api = useApiService(DogResultsApi);

  const [competitionId, setCompetitionId] = React.useState(null);

  const FormSchema = React.useMemo(() => {
    return Yup.object().shape({
      result: Yup.number().nullable().required(t(ETLCodes.Required)),
      qualificationId: Yup.number().nullable().required(t(ETLCodes.Required)),
      showClass: Yup.string().nullable().required(t(ETLCodes.Required)),
      catalogNumber: Yup.number().required(t(ETLCodes.Required)),
    });
  }, [t]);

  const [competitions, cloading] = useReferential((a) => a.getCompetitions(), false, []);
  const [judges, jloading] = useReferential((a) => a.getJudges(competitionId), true, [competitionId]);
  const [results, rloading] = useReferential((a) => a.getResults(), true, []);
  const [classes, clloading] = useReferential((a) => a.getShowClasses(), false, []);
  const [qualifs, qloading] = useReferential((a) => a.getQualifications(), true, []);

  const { data, loading, saveItem, saving, refresh } = useCrudApi<SaveNewResultDto>(
    React.useMemo(
      () => ({
        getApiFn: async (id = selectedResultId) =>
          id > 0 ? await api.getDogCompetitionResult(id) : new SaveNewResultDto({ dogId: dogId }).toJSON(),
        saveApiFn: (d) => api.saveNewResult(d),
      }),
      [api, dogId, selectedResultId]
    ),
    [dogId]
  );
  const save = React.useCallback(
    async (data: any) => {
      await saveItem(data);
      //when done
      onSaved();
    },
    [onSaved, saveItem]
  );

  return (
    <SmallFormGenerator
      onSubmit={save}
      initialValues={data}
      editMode={true}
      validationSchema={FormSchema}
      minLabelWidth={175}
      saving={saving}
      loading={loading}
    >
      <FGListen field="competitionId" onChanged={(value) => setCompetitionId(value)} />
      <FieldGroup columns={[5, 4, 3]}>
        <FGSelectInput name="competitionId" label={t(ETLCodes.Competition)} items={competitions} loading={cloading} />
        <FGSelectInput name="judgeId" label={t(ETLCodes.Judge)} items={judges} loading={jloading} />
        <FGNumberInput name="catalogNumber" label={t(ETLCodes.CatalogNumber)} requiredMark />
        <FGTextAreaInput name="comment" label={t(ETLCodes.Remark)} />
      </FieldGroup>
      <FieldSet title={t(ETLCodes.Results)}>
        <FieldGroup columns={4}>
          <FGSelectInput name="showClass" label={t(ETLCodes.Class)} items={classes} loading={clloading} requiredMark />
          <FGSelectInput
            name="qualificationId"
            label={t(ETLCodes.Qualification)}
            items={qualifs}
            loading={qloading}
            requiredMark
          />
          <FGSelectInput name="result" label={t(ETLCodes.Result)} items={results} loading={rloading} requiredMark />
          <FGCheckboxInput name="standardConformed" label={t(ETLCodes.BreedConformity)} />
        </FieldGroup>
        <FieldGroup columns={5}>
          <FGCheckboxInput name="bestOfBreed" label={t(ETLCodes.BestOfBreed)} />
          <FGCheckboxInput name="bestOfSex" label={t(ETLCodes.BestOfSex)} />
          <FGCheckboxInput name="bestInShow" label={t(ETLCodes.BestInShow)} />
          <FGCheckboxInput name="bestInGroup" label={t(ETLCodes.BestInGroup)} />
          <FGCheckboxInput name="bestVeteran" label={t(ETLCodes.BestVeteran)} />
          <FGCheckboxInput name="bestWorkDog" label={t(ETLCodes.BestWorkDog)} />
          <FGCheckboxInput name="bestYouth" label={t(ETLCodes.BestYouth)} />
          <FGCheckboxInput name="bestMinorPuppy" label={t(ETLCodes.BestMinorPuppy)} />
          <FGCheckboxInput name="bestPuppy" label={t(ETLCodes.BestPuppy)} />
          <FGCheckboxInput name="belgianWinner" label={t(ETLCodes.BelgianWinner)} />
          <FGCheckboxInput name="beneluxWinner" label={t(ETLCodes.BeneluxWinner)} />
          <FGCheckboxInput name="beneluxYouthWinner" label={t(ETLCodes.BeneluxYouthWinner)} />
          <FGCheckboxInput name="beneluxVeteranWinner" label={t(ETLCodes.BeneluxVeteranWinner)} />
          <FGCheckboxInput name="europeanWinner" label={t(ETLCodes.EuropeanWinner)} />
          <FGCheckboxInput name="crufts" label={t(ETLCodes.CruftsQualification)} />
          <FGCheckboxInput name="cac" label={t(ETLCodes.Cac)} />
          <FGCheckboxInput name="cacReserve" label={t(ETLCodes.CacReserve)} />
          <FGCheckboxInput name="cacib" label={t(ETLCodes.Cacib)} />
          <FGCheckboxInput name="cacibReserve" label={t(ETLCodes.CacibReserve)} />
          <FGCheckboxInput name="cacbl" label={t(ETLCodes.Cacbl)} />
          <FGCheckboxInput name="cacblReserve" label={t(ETLCodes.CacblReserve)} />
          <FGCheckboxInput name="cacgpo" label={t(ETLCodes.Cacgpo)} />
          <FGCheckboxInput name="cacgpoReserve" label={t(ETLCodes.CacgpoReserve)} />
          <FGCheckboxInput name="caciag" label={t(ETLCodes.Caciag)} />
          <FGCheckboxInput name="caciagReserve" label={t(ETLCodes.CaciagReserve)} />
          <FGCheckboxInput name="cacil" label={t(ETLCodes.Cacil)} />
          <FGCheckboxInput name="cacilReserve" label={t(ETLCodes.CacilReserve)} />
          <FGCheckboxInput name="caciob" label={t(ETLCodes.Caciob)} />
          <FGCheckboxInput name="caciobReserve" label={t(ETLCodes.CaciobReserve)} />
          <FGCheckboxInput name="cacit" label={t(ETLCodes.Cacit)} />
          <FGCheckboxInput name="cacitReserve" label={t(ETLCodes.CacitReserve)} />
        </FieldGroup>
      </FieldSet>
    </SmallFormGenerator>
  );
};
